import React, { FC, Fragment } from 'react';
import {
  makeStyles,
  createStyles,
  Theme,
  IconButton,
  Typography,
  Table,
  TableHead,
  TableCell,
  TableBody,
  TableRow,
  Tooltip
} from '@material-ui/core';
import { CONFIRMATION_COLOR, PENDING_COLOR, RED, WHITE } from 'constants/colors';
import { TableCellEnd, TableCellMiddle, TableCellStart, TableRowCustom, TableRowCollapse, StatusPayment } from 'components';
import { format } from 'date-fns';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import NumberFormat from 'components/NumberFormat';
import Skeleton from '@material-ui/lab/Skeleton';
import useRouter from 'hooks/useRouter';
import DeleteOutline from '@material-ui/icons/DeleteOutline';
import clsx from 'clsx';

interface Props {
  purchaseInvoice: PurchaseInvoiceModel;
  isLoading: boolean;
  openCollapse: boolean;
  index: number;
  statusInvoice: string;
  indexCollapse: number;
  collapseLoading: boolean;
  handleOpenCollapse: React.MouseEventHandler;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    deleteButton: {
      color: RED,
      width: 30
    },
    tableCellAction: {
      width: 30
    },
    customerName: {
      fontSize: '15px',
      fontWeight: 500,
      fontFamily: 'Rubik',
      fontStyle: 'normal',
      lineHeight: '22px'
    },
    tableCollapse: {
      width: '100%'
    },
    cellCollapse: {
      border: 'none'
    },
    bgYelow: {
      background: PENDING_COLOR
    },
    bgRed: {
      background: RED
    },
    bgBlue: {
      background: CONFIRMATION_COLOR
    },
    statusReturn: {
      padding: '5px 12px 5px 12px',
      borderRadius: '100px',
      fontFamily: 'Rubik',
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: '15px',
      lineHeight: '22px',
      color: WHITE
    }
  })
);

const BodyRow: FC<Props> = props => {
  const { purchaseInvoice, isLoading, statusInvoice, openCollapse, index, indexCollapse, collapseLoading, handleOpenCollapse } = props;
  const { history } = useRouter();
  const classes = useStyles();

  const handleLink = () => {
    if (purchaseInvoice.type && purchaseInvoice.type === 'returnSuplier') {
      history.push('/return-pembelian/detail', { id: purchaseInvoice.id });
    } else {
      history.push('/invoice-pembelian/detail', { id: purchaseInvoice.id });
    }
  };

  return (
    <Fragment>
      <TableRowCustom>
        <TableCellStart>
          {isLoading ? (
            <Skeleton variant='text' width={60} height={25} />
          ) : (
            <IconButton aria-label='expand row' size='small' onClick={handleOpenCollapse}>
              {openCollapse && index === indexCollapse ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCellStart>
        <TableCellMiddle onClick={handleLink}>
          {isLoading ? <Skeleton variant='text' width={60} height={25} /> : '#' + purchaseInvoice.invoiceNumber}
        </TableCellMiddle>

        <TableCellMiddle onClick={handleLink} align='center'>
          {isLoading ? (
            <Skeleton variant='text' width={100} height={25} />
          ) : purchaseInvoice.type === 'returnSuplier' ? (
            <span
              className={clsx(classes.statusReturn, {
                [classes.bgYelow]: (purchaseInvoice.statusPayment as string) === 'Potong Sebagian',
                [classes.bgRed]: (purchaseInvoice.statusPayment as string) === 'Belum dipotong',
                [classes.bgBlue]: (purchaseInvoice.statusPayment as string) === 'Potong Keseluruhan'
              })}
            >
              {purchaseInvoice.statusPayment}
            </span>
          ) : (
            <StatusPayment
              status={purchaseInvoice.statusPayment}
              overdue={purchaseInvoice.dueDate ? new Date().getTime() > new Date(purchaseInvoice.dueDate).getTime() : false}
              totalPrice={purchaseInvoice.totalPrice}
              totalPay={purchaseInvoice.totalPay}
            />
          )}
        </TableCellMiddle>

        <TableCellMiddle align='right' onClick={handleLink}>
          {isLoading ? (
            <Skeleton variant='text' width={120} height={25} />
          ) : (
            <NumberFormat value={purchaseInvoice.totalPrice} prefix={'Rp'} thousandSeparator={true} displayType='text' />
          )}
        </TableCellMiddle>

        <TableCellMiddle onClick={handleLink} align='center'>
          {isLoading ? <Skeleton variant='text' width={120} height={25} /> : purchaseInvoice.totalItem}
        </TableCellMiddle>

        <TableCellEnd onClick={handleLink}>
          {isLoading ? <Skeleton variant='text' width={120} height={25} /> : format(new Date(purchaseInvoice.dueDate), 'dd MMM yyyy')}
        </TableCellEnd>
      </TableRowCustom>
      <TableRowCollapse open={openCollapse && index === indexCollapse} colSpan={5}>
        <Table className={classes.tableCollapse} size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Nama</TableCell>
              <TableCell align='center'>Kuantitas</TableCell>
              <TableCell align='right'>Harga</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {collapseLoading && (
              <TableRow key={index}>
                <TableCell colSpan={3}>
                  <Skeleton variant='text' width='200%' height={25} />
                </TableCell>
              </TableRow>
            )}

            {purchaseInvoice.PurchaseInvoiceItem &&
              purchaseInvoice.PurchaseInvoiceItem.map((value: PurchaseInvoiceItemModel, index) => (
                <TableRow key={index}>
                  <TableCell className={classes.cellCollapse}>{value.Product ? value.Product.productName : '-'}</TableCell>
                  <TableCell align='center' className={classes.cellCollapse}>
                    {value.Product ? value.totalItem + ' ' + value.typeUnit : 0}
                  </TableCell>
                  <TableCell align='right' className={classes.cellCollapse}>
                    <NumberFormat value={value.totalPrice} prefix={'Rp'} thousandSeparator={true} displayType='text' />
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableRowCollapse>
    </Fragment>
  );
};

export default BodyRow;
